import { ArrowRight, Check, X, Sparkles, DollarSign, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PublicShell } from "@/views/PublicPages";

const COMPETITORS = {
  trello: {
    name: "Trello",
    price: "$12.50",
    tagline: "Everything you love about Trello boards — plus AI, chat, Gantt and CRM built in, for $4.51 less per user.",
    rows: [
      ["Kanban boards", true, true],
      ["AI task assistant (natural language)", true, false],
      ["Built-in team chat & DMs", true, false],
      ["Gantt / Timeline view", true, "Paid add-on"],
      ["Time tracking", true, false],
      ["Built-in CRM & deals pipeline", "Bundle", false],
      ["Automation rules", true, "Limited (Butler)"],
      ["Analytics dashboard", true, "Limited"],
    ],
    reasons: [
      { icon: Sparkles, title: "AI does the busywork", text: "Type 'Call John tomorrow at 3pm about the budget' and get a structured task with due date and priority. Trello has no built-in AI assistant." },
      { icon: Zap, title: "No Power-Up juggling", text: "Gantt charts, time tracking, automations and chat are native in Opero Smart. In Trello most of these require paid third-party Power-Ups." },
      { icon: DollarSign, title: "36% cheaper", text: "Opero Smart Pro is $7.99/user/mo vs Trello Premium at $12.50. For a 10-person team that's $541 saved every year." },
    ],
    faq: "Trello is great for simple boards, but teams quickly outgrow it: no native Gantt, no time tracking, and automation limits. Opero Smart includes all of that out of the box with a generous free plan, so migrating is a straight upgrade.",
  },
  asana: {
    name: "Asana",
    price: "$10.99",
    tagline: "Asana's power without the complexity — plus built-in AI, chat and CRM at 27% lower cost per user.",
    rows: [
      ["Kanban boards", true, true],
      ["AI task assistant (natural language)", true, "Paid tier only"],
      ["Built-in team chat & DMs", true, false],
      ["Gantt / Timeline view", true, "Paid"],
      ["Time tracking", true, "Advanced tier only"],
      ["Built-in CRM & deals pipeline", "Bundle", false],
      ["Automation rules", true, true],
      ["Simple onboarding (< 5 min)", true, false],
    ],
    reasons: [
      { icon: Zap, title: "Productive in minutes, not weeks", text: "Asana's depth comes with a steep learning curve and endless configuration. Opero Smart gives you boards, calendar and analytics that just work from day one." },
      { icon: Sparkles, title: "Chat where the work is", text: "Asana teams still pay for Slack. Opero Smart has channels, DMs and task discussions built in — context stays attached to the work." },
      { icon: DollarSign, title: "27% cheaper", text: "Opero Smart Pro is $7.99/user/mo vs Asana Starter at $10.99 — and time tracking doesn't require Asana's expensive Advanced tier." },
    ],
    faq: "Asana is built for enterprise process management, which means smaller teams pay for complexity they never use. Opero Smart keeps the powerful views (Kanban, Gantt, Calendar, Workload) while staying simple enough that your whole team actually uses it.",
  },
  notion: {
    name: "Notion",
    price: "$9.50",
    tagline: "Notion is a wiki that does tasks. Opero Smart is a project manager that ships work — with real boards, real-time collaboration and CRM.",
    rows: [
      ["Purpose-built Kanban boards", true, "Database views"],
      ["AI task assistant (natural language)", true, "Paid AI add-on"],
      ["Built-in team chat & DMs", true, false],
      ["Gantt / Timeline view", true, "Basic timeline"],
      ["Time tracking", true, false],
      ["Built-in CRM & deals pipeline", "Bundle", "DIY templates"],
      ["Automation rules & due-date reminders", true, "Limited"],
      ["Real-time presence & live cursors", true, true],
    ],
    reasons: [
      { icon: Zap, title: "Structure beats blank pages", text: "Notion gives you a blank canvas — someone has to build and maintain the system. Opero Smart ships with boards, priorities, workload and analytics that work instantly." },
      { icon: Sparkles, title: "A real CRM, not a template", text: "Notion CRMs are hand-built databases that break as you grow. Opero Smart's Business Bundle includes a drag-and-drop deals pipeline, contacts and companies." },
      { icon: DollarSign, title: "Less cost, fewer add-ons", text: "Notion Plus + Notion AI costs more than Opero Smart Pro at $7.99 — and you still won't have time tracking or team chat." },
    ],
    faq: "Notion is fantastic for documentation and wikis. But dedicated project management needs opinionated structure: WIP-friendly boards, dependencies, time tracking and workload views. Many teams keep Notion for docs and run their projects in Opero Smart.",
  },
  monday: {
    name: "Monday.com",
    price: "$12.00",
    tagline: "Monday's colorful boards without the per-seat sticker shock — plus built-in AI, chat and CRM starting at $7.99.",
    rows: [
      ["Kanban boards", true, true],
      ["AI task assistant (natural language)", true, "Paid AI credits"],
      ["Built-in team chat & DMs", true, false],
      ["Gantt / Timeline view", true, "Standard tier+"],
      ["Time tracking", true, "Pro tier only"],
      ["Built-in CRM & deals pipeline", "Bundle", "Separate product"],
      ["Automation rules", true, "Monthly action limits"],
      ["No minimum seats", true, "3-seat minimum"],
    ],
    reasons: [
      { icon: DollarSign, title: "No seat minimums or tier traps", text: "Monday requires 3 seats minimum and locks time tracking behind its Pro tier ($19/seat). Opero Smart includes time tracking in Pro at $7.99 — for a 5-person team that's over $660/year saved." },
      { icon: Zap, title: "Unlimited automations", text: "Monday caps automation actions per month by tier, so busy boards hit the wall. Opero Smart's automation rules run without monthly action limits." },
      { icon: Sparkles, title: "CRM included, not a second subscription", text: "Monday sells its CRM as a separate product with separate pricing. Opero Smart's Business Bundle adds a full deals pipeline for $12.99 total — less than Monday's work management alone." },
    ],
    faq: "Monday.com is polished and flexible, but costs escalate fast: seat minimums, tier-gated features, and a separately-priced CRM. Opero Smart keeps one simple price, includes chat and time tracking, and bundles the CRM — which is why cost-conscious teams switch.",
  },
  clickup: {
    name: "ClickUp",
    price: "$10.00",
    tagline: "Everything ClickUp promises, without the clutter and the learning curve — clean, fast and AI-first.",
    rows: [
      ["Kanban boards", true, true],
      ["AI task assistant (natural language)", true, "Paid AI add-on"],
      ["Built-in team chat & DMs", true, true],
      ["Gantt / Timeline view", true, true],
      ["Time tracking", true, true],
      ["Built-in CRM & deals pipeline", "Bundle", "DIY templates"],
      ["Fast, uncluttered interface", true, "Known performance issues"],
      ["Productive in under 5 minutes", true, false],
    ],
    reasons: [
      { icon: Zap, title: "Simplicity is the feature", text: "ClickUp's 'everything app' approach means dozens of views, settings and toggles — teams spend weeks configuring instead of shipping. Opero Smart gives you the views that matter, working out of the box." },
      { icon: Sparkles, title: "Speed you can feel", text: "ClickUp's biggest user complaint is sluggish load times on large workspaces. Opero Smart is built lean with real-time WebSockets — boards update instantly, even with your whole team online." },
      { icon: DollarSign, title: "AI without the add-on fee", text: "ClickUp charges extra per user for ClickUp Brain on top of the plan price. Opero Smart's AI task assistant is included in Pro at $7.99 — less than ClickUp Unlimited before AI." },
    ],
    faq: "ClickUp wins feature-checklist comparisons but loses on daily experience: cluttered UX, performance complaints, and a steep learning curve are the most common reasons teams leave. Opero Smart covers the core workflows (boards, Gantt, time tracking, chat, automations, CRM) in an interface your team will actually enjoy using.",
  },
};

const Cell = ({ v, isOpero }) => {
  if (v === true) return <Check className={`h-4 w-4 mx-auto ${isOpero ? "text-primary" : "text-emerald-500"}`} />;
  if (v === false) return <X className="h-4 w-4 mx-auto text-muted-foreground/40" />;
  if (v === "Bundle") return <span className="inline-flex items-center gap-1 text-xs font-medium text-primary"><Check className="h-4 w-4" />Bundle</span>;
  return <span className="text-xs text-muted-foreground">{v}</span>;
};

export const ComparisonPage = ({ competitor }) => {
  const data = COMPETITORS[competitor];
  if (!data) {
    return (
      <PublicShell title="Compare — Opero Smart" description="Compare Opero Smart with Trello, Asana and Notion.">
        <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="comparison-index">
          <h1 className="font-heading text-3xl font-bold mb-6">Compare Opero Smart</h1>
          <div className="flex flex-wrap justify-center gap-3">
            {Object.keys(COMPETITORS).map((k) => (
              <Button key={k} asChild variant="outline"><a href={`/vs/${k}`}>vs {COMPETITORS[k].name}</a></Button>
            ))}
          </div>
        </div>
      </PublicShell>
    );
  }
  const year = new Date().getFullYear();
  return (
    <PublicShell
      title={`Opero Smart vs ${data.name} (${year}) — Feature & Pricing Comparison`}
      description={`${data.name} alternative: compare features and pricing. ${data.tagline}`}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid={`comparison-page-${competitor}`}>
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4">Comparison</Badge>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="comparison-title">Opero Smart vs {data.name}</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-6">{data.tagline}</p>
          <div className="flex justify-center gap-3">
            <Button asChild data-testid="comparison-hero-cta"><a href="/">Try Opero Smart free<ArrowRight className="h-4 w-4 ml-1.5" /></a></Button>
            <Button asChild variant="outline"><a href="/#pricing">See pricing</a></Button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-2xl border bg-card mb-14" data-testid="comparison-vs-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left p-4 font-medium text-muted-foreground">Feature</th>
                <th className="p-4 font-bold text-primary">Opero Smart</th>
                <th className="p-4 font-medium text-muted-foreground">{data.name}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(([label, us, them], i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="p-4 font-medium">{label}</td>
                  <td className="p-4 text-center"><Cell v={us} isOpero /></td>
                  <td className="p-4 text-center"><Cell v={them} /></td>
                </tr>
              ))}
              <tr className="bg-muted/40">
                <td className="p-4 font-semibold">Starting price (per user/mo)</td>
                <td className="p-4 text-center font-bold text-primary">$7.99</td>
                <td className="p-4 text-center text-muted-foreground">{data.price}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 className="font-heading text-2xl font-bold text-center mb-8">Why teams switch from {data.name}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-14">
          {data.reasons.map((r, i) => {
            const Icon = r.icon;
            return (
              <div key={i} className="bg-card border rounded-2xl p-6" data-testid={`switch-reason-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-3"><Icon className="h-5 w-5 text-primary" /></div>
                <h3 className="font-semibold text-sm mb-2">{r.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{r.text}</p>
              </div>
            );
          })}
        </div>

        <div className="rounded-2xl bg-muted/40 border p-6 sm:p-8 mb-14">
          <h2 className="font-heading text-lg font-bold mb-3">Is Opero Smart a good {data.name} alternative?</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">{data.faq}</p>
        </div>

        <div className="text-center rounded-2xl border bg-gradient-to-br from-primary/10 to-purple-500/5 p-8 sm:p-10">
          <h2 className="font-heading text-2xl font-bold mb-2">Ready to make the switch?</h2>
          <p className="text-sm text-muted-foreground mb-5">Free forever plan. No credit card. Import your workflow in minutes with templates.</p>
          <Button asChild size="lg" data-testid="comparison-bottom-cta"><a href="/">Get started free<ArrowRight className="h-4 w-4 ml-1.5" /></a></Button>
          <div className="flex justify-center gap-4 mt-6 text-xs text-muted-foreground">
            {Object.keys(COMPETITORS).filter((k) => k !== competitor).map((k) => (
              <a key={k} href={`/vs/${k}`} className="hover:text-foreground underline underline-offset-2" data-testid={`comparison-link-${k}`}>Opero Smart vs {COMPETITORS[k].name}</a>
            ))}
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground text-center mt-6">Competitor pricing and features based on publicly listed plans, {year}. Trademarks belong to their respective owners.</p>
      </div>
    </PublicShell>
  );
};
